/* eslint-disable import/no-cycle */
/* eslint-disable no-unused-vars */
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { notification } from '../../components';
import { serializeQueryParams, getUserDetails } from '../../utils';

// Define a service using a base URL and expected endpoints

export const mriPatientApi = createApi({
  reducerPath: 'mriPatientApi',
  baseQuery: fetchBaseQuery({
    baseUrl: process.env[`REACT_APP_API_URL_${process.env.REACT_APP_ENVIRONMENT?.toUpperCase()}`],
    // baseUrl: 'http://localhost:3000/api/v1',
    prepareHeaders: (headers, { getState }) => {
      headers.set('Authorization', `Bearer ${localStorage.getItem('token')}`);
      headers.set('Content-Language', 'en');
      return headers;
    },
  }),
  tagTypes: ['cmsData', 'specialistProviders', 'availability', 'appointment', 'profile'],
  endpoints: (builder) => ({

    login: builder.mutation({
      query: (body) => ({
        url: '/patient/login',
        method: 'POST',
        body,
      }),
      onQueryStarted(body, { queryFulfilled }) {
        queryFulfilled
          .catch(({ error }) => notification({
            title: 'Invalid credentials!',
            description: error?.data?.message || 'Either your email or password is invalid',
            type: 'error',
          }));
      },
    }),
    verify: builder.mutation({
      query: ({ verifyToken, otp, uuid }) => ({
        url: '/patient/verify',
        method: 'PUT',
        body: {
          verifyToken,
          otp,
          uuid: uuid || localStorage.getItem('uuid'),
        },
      }),
      onQueryStarted(body, { queryFulfilled }) {
        queryFulfilled
          .catch(({ error }) => notification({
            title: 'Invalid OTP!',
            description: error?.data?.message || 'Please check the entered OTP.',
            type: 'error',
          }));
      },
    }),
    resendOTP: builder.mutation({
      query: ({ verifyToken }) => ({
        url: '/patient/resendTwoFactorCode',
        method: 'PUT',
        body: {
          verifyToken,
        },
      }),
      onQueryStarted(body, { queryFulfilled }) {
        queryFulfilled
          .then(() => {
            notification({
              title: 'OTP sent successfully!',
              description: 'OTP has been sent to registered email',
              type: 'success',
            });
          })
          .catch(({ error }) => notification({
            title: 'Something wen wrong!',
            description: error?.data?.message || 'Please try after some time.',
            type: 'error',
          }));
      },
    }),
    register: builder.mutation({
      query: (body) => ({
        url: '/patient/signup',
        method: 'POST',
        body,
      }),
      onQueryStarted(body, { queryFulfilled }) {
        queryFulfilled
          .catch(({ error }) => {
            notification({
              title: 'Unable to create account!',
              description: error?.message || error?.data?.message || error?.data?.message || 'please try after some time.',
              type: 'error',
            });
          });
      },
    }),
    // get profile
    getProfile: builder.query({
      query: ({ id }) => ({
        url: '/patient/profile',
        method: 'GET',
      }),
      providesTags: ['profile'],
      transformResponse: (data) => data?.response?.data,
      onQueryStarted(body, { queryFulfilled }) {
        queryFulfilled
          .catch(({ error }) => {
            // console.log('profile', error);
          });
      },
    }),
    updateProfile: builder.mutation({
      query: ({ patientId, ...body }) => ({
        url: `/patient/profile/${patientId}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['profile'],
      onQueryStarted(body, { queryFulfilled }) {
        queryFulfilled
          .catch(({ error }) => {
            notification({
              title: 'Unable to update profile!',
              description: error?.message || error?.data?.message || error?.data?.message || 'Please try after some time.',
              type: 'error',
            });
          });
      },
    }),
    forgotPassword: builder.mutation({
      query: (body) => ({
        url: '/patient/forgotPassword',
        method: 'PUT',
        body,
      }),
      onQueryStarted(body, { queryFulfilled }) {
        queryFulfilled
          .then(() => notification({
            title: body?.isUsingEmail ? 'Email sent!' : 'OTP Send',
            description: body?.isUsingEmail ? 'Please check your email for passowrd recovery steps.' : 'Please check your registered mobile number for one time password',
            type: 'success',
          }))
          .catch(({ error }) => notification({
            title: 'Something went wrong!',
            description: error?.message || error?.data?.message || 'Please try after some time.',
            type: 'error',
          }));
      },
    }),
    resetPassword: builder.mutation({
      query: ({ newPassword }) => ({
        url: '/patient/resetPassword',
        method: 'PUT',
        body: { newPassword },
      }),
      onQueryStarted(body, { queryFulfilled }) {
        queryFulfilled
          .then(() => notification({
            title: 'Password Updated!',
            description: 'Password has been changed successfully.',
            type: 'success',
          }))
          .catch(({ error }) => {
            notification({
              title: 'Unable to update password!',
              description: error?.data?.message || 'Please try after sometime.',
              type: 'error',
            });
          });
      },
    }),

    // -----mobile verification----
    verifyMobile: builder.mutation({
      query: (body) => ({
        url: '/patient/mobile/otp',
        method: 'POST',
        body,
      }),
      onQueryStarted(body, { queryFulfilled }) {
        queryFulfilled
          .then(() => notification({
            title: 'OTP Sent!',
            description: 'An OTP has been sent to your mobile number for verification.',
            type: 'success',
          }))
          .catch(({ error }) => {
            notification({
              title: 'Unable to send OTP!',
              description: error?.data?.message || 'Please try after sometime.',
              type: 'error',
            });
          });
      },
    }),
    verifyMobileOTP: builder.mutation({
      query: ({ otp, patientData = {}, patientId = '' }) => ({
        url: '/patient/mobile/verify',
        method: 'PUT',
        body: { otp, patientId },
      }),
      onQueryStarted(body, { queryFulfilled }) {
        queryFulfilled
          .then(() => notification({
            title: 'Phone verified!',
            description: 'Your phone number has been verified.',
            type: 'success',
          }))
          .catch(({ error }) => {
            notification({
              title: 'Wrong OTP!',
              description: error?.data?.message || 'Please try after sometime.',
              type: 'error',
            });
          });
      },
    }),
    resendVerifyOTP: builder.mutation({
      query: (body) => ({
        url: '/patient/mobile/resend/otp',
        method: 'PUT',
        body,
      }),
      onQueryStarted(body, { queryFulfilled }) {
        queryFulfilled
          .then(() => notification({
            title: 'OTP Sent!',
            description: 'OTP sent to your mobile number.',
            type: 'success',
          }))
          .catch(({ error }) => {
            notification({
              title: 'Unable to send OTP!',
              description: error?.data?.message || 'Please try after sometime.',
              type: 'error',
            });
          });
      },
    }),
    getCmsData: builder.query({
      query: () => ({
        url: '/patient/cmsData',
        method: 'GET',
      }),
      providesTags: ['cmsData'],
    }),
    getBodyParts: builder.query({
      query: ({ specialistId }) => ({
        url: `/specialist/${specialistId}/bodyParts`,
        method: 'GET',
      }),
      providesTags: ['bodyParts'],
    }),

    // upload file
    uploadFile: builder.mutation({
      query: (body) => ({
        url: '/common/uploadFile',
        method: 'POST',
        body,
      }),
    }),

    // stripe checkout
    checkout: builder.mutation({
      query: (body) => ({
        url: '/stripe/appointment/checkout',
        method: 'POST',
        body,
      }),
      providesTags: ['appointment'],
      onQueryStarted(body, { queryFulfilled }) {
        queryFulfilled
          .catch(({ error }) => {
            notification({
              title: 'Unable to checkout!',
              description: error?.data?.message || 'Please try after sometime.',
              type: 'error',
            });
          });
      },
    }),

    // create booking / appointment
    createAppointment: builder.mutation({
      query: (body) => ({
        url: '/appointment/patient',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['appointments', 'profile'],
      onQueryStarted(body, { queryFulfilled }) {
        queryFulfilled
          .then(() => notification({
            title: 'Appointment Created!',
            description: 'Your appointment has been created',
            type: 'success',
          }))
          .catch(({ error }) => {
            notification({
              title: 'Unable to create appointment!',
              description: error?.data?.message || 'Please try after sometime.',
              type: 'error',
            });
          });
      },
    }),

    // get appointmetn list
    getAppointments: builder.query({
      query: () => ({
        url: '/appointments/patient?limit=200',
        method: 'GET',
      }),
      providesTags: ['appointments'],
      transformResponse: (data) => data?.response?.data,
    }),

    // get appointment
    getAppointment: builder.query({
      query: ({ id, token }) => ({
        url: `/appointment/${id}/patient`,
        method: 'GET',
      }),
      providesTags: ['appointments'],
      transformResponse: (data) => data?.response?.data,
    }),

    // get appointment data for confirmation
    getConfirmation: builder.query({
      query: ({ id, token }) => ({
        url: `/appointment/appointmentId/${id}/patient`,
        method: 'GET',
      }),
      providesTags: ['appointments'],
      transformResponse: (data) => data?.response?.data,
    }),

    // cancel appointment
    cancelAppointment: builder.mutation({
      query: ({ appointmentId = '', reason = '' }) => ({
        url: `/appointment/${appointmentId}/cancel`,
        method: 'PUT',
        body: { reason },
      }),
      invalidatesTags: ['appointments'],
      onQueryStarted(body, { queryFulfilled }) {
        queryFulfilled
          .then(() => notification({
            title: 'Appointment Canceled',
            description: 'Your appointment has been canceled',
            type: 'success',
          }))
          .catch(({ error }) => {
            notification({
              title: 'Unable to cancel appointment!',
              description: error?.data?.message || 'Please try after sometime.',
              type: 'error',
            });
          });
      },
    }),

    // reschedule appointment
    rescheduleAppointment: builder.mutation({
      query: ({ appointmentId, ...body }) => ({
        url: `/appointment/${appointmentId}/reschedule`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['appointments'],
      onQueryStarted(body, { queryFulfilled }) {
        queryFulfilled
          .then(() => notification({
            title: 'Appointment Rescheduled',
            description: 'Your appointment has been reschedule',
            type: 'success',
          }))
          .catch(({ error }) => {
            notification({
              title: 'Unable to reschedule appointment!',
              description: error?.data?.message || 'Please try after sometime.',
              type: 'error',
            });
          });
      },
    }),

    // get the search results
    getSpecialistList: builder.query({
      query: ({
        location, bodyPart = [], examType = [], miles, sortBy, sortOrder,
      }) => ({
        url: '/specialist/list?skip=0&limit=100',
        method: 'POST',
        body: {
          location,
          // bodyPart,
          examType,
          ...(bodyPart?.length > 0 && { bodyPart }),
          ...(miles && { miles }),
          ...(sortBy && { sortBy }),
          ...(sortOrder && { sortOrder }),
        },
      }),
      providesTags: ['specialistProviders'],
      transformResponse: (data) => data?.response?.data,
    }),

    // get the pcp lists
    getPcpList: builder.query({
      query: () => ({
        url: '/pcp/getDropDownData?limit=100',
        method: 'GET',
        // body: {
        //   location,
        //   bodyPart,
        //   examType,
        // },
      }),
      providesTags: ['pcp'],
      transformResponse: (data) => data?.response?.data,
    }),

    // get the availability
    getAvailability: builder.query({
      query: ({ ...queryParams }) => ({
        url: `/provider/availability?${serializeQueryParams({ ...queryParams })}`,
        method: 'GET',
      }),
      providesTags: ['availability'],
      transformResponse: (data) => data?.response,
      onQueryStarted(body, { queryFulfilled }) {
        queryFulfilled
          .catch(({ error }) => {
            notification({
              title: 'Unable to get availability',
              description: error?.data?.message || 'Please try after sometime.',
              type: 'error',
            });
          });
      },
    }),

    updateAppointment: builder.mutation({
      query: ({ appointmentId = '', data }) => ({
        url: `/appointment/patient/${appointmentId}/update`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: ['appointments'],
      onQueryStarted(body, { queryFulfilled }) {
        queryFulfilled
          .then(() => notification({
            title: 'Appointment Updated',
            description: 'Your appointment has been updated',
            type: 'success',
          }))
          .catch(({ error }) => {
            notification({
              title: 'Unable to update appointment!',
              description: error?.data?.message || 'Please try after sometime.',
              type: 'error',
            });
          });
      },
    }),

  }),

});

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const {
  useLoginMutation,
  useVerifyMutation,
  useResendOTPMutation,
  useRegisterMutation,
  useUpdateProfileMutation,
  useGetProfileQuery,
  useForgotPasswordMutation,
  useResetPasswordMutation,
  useGetCmsDataQuery,
  useGetBodyPartsQuery,
  useGetPcpListQuery,
  useLazyGetSpecialistListQuery,
  useCreateAppointmentMutation,
  useLazyGetAvailabilityQuery,
  useCheckoutMutation,
  useUploadFileMutation,
  useGetAppointmentQuery,
  useLazyGetAppointmentQuery,
  useGetAppointmentsQuery,
  useVerifyMobileMutation,
  useVerifyMobileOTPMutation,
  useResendVerifyOTPMutation,
  useCancelAppointmentMutation,
  useRescheduleAppointmentMutation,
  useLazyGetConfirmationQuery,
  useUpdateAppointmentMutation,
} = mriPatientApi;
